/**
 * InterviewPractice Page
 *
 * Multi-phase virtual interview accessible from the Resume Tuner.
 * Phases: setup → interview → debrief
 * Supports text mode (typed answers) and voice mode (Gemini Live voice conversation).
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  startInterviewPractice,
  submitInterviewAnswer,
  generateInterviewDebrief,
} from '../../services/interviewPractice';
import {
  InterviewDuration,
  InterviewSession,
  InterviewDebrief,
  DURATION_TIERS,
} from '../../types/interviewPractice';
import { useGeminiLive, TranscriptEntry, GeminiLiveOptions } from '../../hooks/useGeminiLive';
import { InterviewDebriefView } from '../../components/InterviewPractice/InterviewDebriefView';
import './InterviewPractice.css';

type Phase = 'setup' | 'interview' | 'debrief';
type InputMode = 'text' | 'voice';

export function InterviewPractice() {
  const { user } = useAuth();
  void user; // used for auth guard context
  const { setPageCrumbs } = useBreadcrumbs();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Job info passed from Resume Tuner via router state
  const passedState = (location.state as any) || {};
  const initialJobTitle = passedState.jobTitle || '';
  const initialCompany = passedState.company || '';
  const initialJobDescription = passedState.jobDescription || '';

  // Phase
  const [phase, setPhase] = useState<Phase>('setup');

  // Input mode: text (typed answers) or voice (Gemini Live conversation)
  const [inputMode, setInputMode] = useState<InputMode>('text');

  // Setup state
  const [jobTitle, setJobTitle] = useState(initialJobTitle);
  const [company, setCompany] = useState(initialCompany);
  const [jobDescription, setJobDescription] = useState(initialJobDescription);
  const [duration, setDuration] = useState<InterviewDuration>(10);

  // Interview state
  const [session, setSession] = useState<InterviewSession | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [followUp, setFollowUp] = useState('');
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());

  // Timer
  const [timeLeftSec, setTimeLeftSec] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Voice mode state
  const [voiceSessionStarted, setVoiceSessionStarted] = useState(false);
  const voiceTranscriptRef = useRef<TranscriptEntry[]>([]);

  // Debrief
  const [debrief, setDebrief] = useState<InterviewDebrief | null>(null);

  // General
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Gemini Live options for voice interview mode
  const geminiLiveOptions = useMemo<GeminiLiveOptions | undefined>(() => {
    if (inputMode !== 'voice' || !session) return undefined;
    return {
      contextFunction: 'getInterviewVoiceContext',
      contextParams: { sessionId: session.id },
      skipChatHistory: true,
      initialPrompt: language === 'fr'
        ? `[Début de l'entretien d'embauche simulé. Présentez-vous brièvement et posez la première question.]`
        : `[Starting the mock job interview. Introduce yourself briefly and ask the first question.]`,
    };
  }, [inputMode, session, language]);

  // Gemini Live hook (only active in voice mode)
  const {
    state: voiceState,
    connect: voiceConnect,
    disconnect: voiceDisconnect,
    transcript: voiceTranscript,
    error: voiceError,
    micLevel,
    availableDevices,
    refreshDevices,
  } = useGeminiLive(language, undefined, geminiLiveOptions);

  // Sync transcript ref
  useEffect(() => {
    voiceTranscriptRef.current = voiceTranscript;
  }, [voiceTranscript]);

  // Breadcrumbs
  useEffect(() => {
    const label =
      phase === 'setup'
        ? t('interviewPractice.breadcrumb.setup')
        : phase === 'interview'
          ? t('interviewPractice.breadcrumb.interview')
          : t('interviewPractice.breadcrumb.debrief');
    setPageCrumbs([
      { label: t('nav.resumeTuner'), to: '/resume-tuner' },
      { label },
    ]);
  }, [phase, setPageCrumbs, t]);

  // Countdown timer
  useEffect(() => {
    if (phase !== 'interview' || !session) return;
    setTimeLeftSec(session.durationMinutes * 60);

    timerRef.current = setInterval(() => {
      setTimeLeftSec((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          handleTimerEnd();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, session]);

  /* ---------------------------------------------------------------- */
  /*  Handlers                                                         */
  /* ---------------------------------------------------------------- */

  const handleStart = async () => {
    if (!jobTitle.trim() || !jobDescription.trim()) {
      setError(t('interviewPractice.errors.missingFields'));
      return;
    }
    try {
      setLoading(true);
      setError('');
      const sess = await startInterviewPractice(jobTitle, company, jobDescription, duration);
      setSession(sess);
      setCurrentQuestionIndex(0);
      setQuestionStartTime(Date.now());
      setPhase('interview');

      // In voice mode, refresh devices so user can pick a mic
      if (inputMode === 'voice') {
        refreshDevices();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start interview');
    } finally {
      setLoading(false);
    }
  };

  /** Start the Gemini Live voice session for the interview. */
  const handleStartVoice = async (deviceId?: string) => {
    try {
      setError('');
      setVoiceSessionStarted(true);
      await voiceConnect(deviceId);
    } catch (err: any) {
      setError(err.message || 'Failed to start voice session');
      setVoiceSessionStarted(false);
    }
  };

  /** End the voice interview and move to debrief. */
  const handleEndVoiceInterview = async () => {
    if (timerRef.current) clearInterval(timerRef.current);

    // Disconnect the voice session
    await voiceDisconnect();

    // Build transcript text from voice entries
    const transcriptText = voiceTranscriptRef.current
      .map(entry => `${entry.role === 'user' ? 'Candidate' : 'Interviewer'}: ${entry.text}`)
      .join('\n\n');

    if (!session) return;

    try {
      setLoading(true);
      // Generate debrief with voice transcript
      const d = await generateInterviewDebrief(session.id, transcriptText || undefined);
      setDebrief(d);
      setPhase('debrief');
    } catch (err: any) {
      setError(err.message || 'Failed to generate debrief');
      setPhase('debrief');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!session || !answerText.trim()) return;
    const q = session.questions[currentQuestionIndex];
    const elapsed = Math.round((Date.now() - questionStartTime) / 1000);

    try {
      setSubmitting(true);
      setFollowUp('');
      const resp = await submitInterviewAnswer(session.id, q.id, answerText, elapsed);

      if (resp.followUp) setFollowUp(resp.followUp);

      // Move to next question or finish
      if (resp.interviewComplete || currentQuestionIndex >= session.questions.length - 1) {
        if (timerRef.current) clearInterval(timerRef.current);
        await handleFinish();
      } else {
        setCurrentQuestionIndex((i) => i + 1);
        setAnswerText('');
        setQuestionStartTime(Date.now());
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTimerEnd = useCallback(async () => {
    if (inputMode === 'voice') {
      // For voice mode, end the voice session and go to debrief
      handleEndVoiceInterview();
      return;
    }
    // Auto-submit whatever is in the text box and move to debrief
    if (session && answerText.trim()) {
      const q = session.questions[currentQuestionIndex];
      const elapsed = Math.round((Date.now() - questionStartTime) / 1000);
      try {
        await submitInterviewAnswer(session.id, q.id, answerText, elapsed);
      } catch { /* best effort */ }
    }
    await handleFinish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, answerText, currentQuestionIndex, questionStartTime, inputMode]);

  const handleFinish = async () => {
    if (!session) return;
    try {
      setLoading(true);
      const d = await generateInterviewDebrief(session.id);
      setDebrief(d);
      setPhase('debrief');
    } catch (err: any) {
      setError(err.message || 'Failed to generate debrief');
      setPhase('debrief'); // still show debrief phase even on error
    } finally {
      setLoading(false);
    }
  };

  const handleEndEarly = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (inputMode === 'voice') {
      await handleEndVoiceInterview();
    } else {
      await handleFinish();
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Helpers                                                          */
  /* ---------------------------------------------------------------- */

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const currentQuestion = session?.questions[currentQuestionIndex];
  const progress = session ? ((currentQuestionIndex + 1) / session.questions.length) * 100 : 0;

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  return (
    <div className="interview-practice-container">
      {/* -------- SETUP PHASE -------- */}
      {phase === 'setup' && (
        <div className="ip-setup">
          <h1>{t('interviewPractice.title')}</h1>
          <p className="ip-subtitle">{t('interviewPractice.subtitle')}</p>

          {error && <div className="ip-error">{error}</div>}

          <div className="ip-form">
            <label htmlFor="ip-jobTitle">{t('interviewPractice.jobTitle')}</label>
            <input
              id="ip-jobTitle"
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder={t('interviewPractice.jobTitlePlaceholder')}
            />

            <label htmlFor="ip-company">{t('interviewPractice.company')}</label>
            <input
              id="ip-company"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder={t('interviewPractice.companyPlaceholder')}
            />

            <label htmlFor="ip-jobDesc">{t('interviewPractice.jobDescription')}</label>
            <textarea
              id="ip-jobDesc"
              rows={6}
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder={t('interviewPractice.jobDescPlaceholder')}
            />

            <label>{t('interviewPractice.duration')}</label>
            <div className="ip-duration-options">
              {([10, 15, 20, 30] as InterviewDuration[]).map((d) => {
                const tier = DURATION_TIERS[d];
                const isPremium = tier === 'premium';
                return (
                  <button
                    key={d}
                    className={`ip-duration-btn ${duration === d ? 'active' : ''} ${isPremium ? 'premium' : ''}`}
                    onClick={() => !isPremium && setDuration(d)}
                    disabled={isPremium}
                    title={isPremium ? t('interviewPractice.premiumOnly') : ''}
                  >
                    {d} min
                    {isPremium && <span className="ip-premium-badge">PRO</span>}
                  </button>
                );
              })}
            </div>

            {/* Input mode toggle: text vs voice */}
            <label>{t('interviewPractice.inputMode')}</label>
            <div className="ip-mode-toggle">
              <button
                className={`ip-mode-btn ${inputMode === 'text' ? 'active' : ''}`}
                onClick={() => setInputMode('text')}
              >
                <span className="ip-mode-icon">&#9998;</span>
                {t('interviewPractice.textMode')}
              </button>
              <button
                className={`ip-mode-btn ${inputMode === 'voice' ? 'active' : ''}`}
                onClick={() => setInputMode('voice')}
              >
                <span className="ip-mode-icon">&#127908;</span>
                {t('interviewPractice.voiceMode')}
              </button>
            </div>
            {inputMode === 'voice' && (
              <p className="ip-voice-hint">{t('interviewPractice.voiceHint')}</p>
            )}

            <button
              className="btn btn-primary ip-start-btn"
              onClick={handleStart}
              disabled={loading}
            >
              {loading ? t('interviewPractice.starting') : t('interviewPractice.startInterview')}
            </button>

            <button className="btn btn-secondary ip-back-btn" onClick={() => navigate('/resume-tuner')}>
              ← {t('common.back')}
            </button>
          </div>
        </div>
      )}

      {/* -------- INTERVIEW PHASE -------- */}
      {phase === 'interview' && session && (
        <div className="ip-interview">
          {/* Timer bar (shown for both modes) */}
          <div className="ip-timer-bar">
            <div className="ip-timer-text">
              <span className={timeLeftSec < 60 ? 'ip-timer-warning' : ''}>
                {formatTime(timeLeftSec)}
              </span>
            </div>
            <div className="ip-progress-bar">
              {inputMode === 'text' ? (
                <div className="ip-progress-fill" style={{ width: `${progress}%` }} />
              ) : (
                <div className="ip-progress-fill ip-progress-voice" style={{ width: `${Math.max(0, 100 - (timeLeftSec / (session.durationMinutes * 60)) * 100)}%` }} />
              )}
            </div>
            <span className="ip-progress-label">
              {inputMode === 'text'
                ? t('interviewPractice.questionOf')
                    .replace('{current}', String(currentQuestionIndex + 1))
                    .replace('{total}', String(session.questions.length))
                : t('interviewPractice.voiceModeLabel')}
            </span>
          </div>

          {error && <div className="ip-error">{error}</div>}
          {voiceError && <div className="ip-error">{voiceError}</div>}

          {/* ---- TEXT MODE ---- */}
          {inputMode === 'text' && currentQuestion && (
            <>
              {/* Follow-up from previous answer */}
              {followUp && (
                <div className="ip-followup">
                  <strong>{t('interviewPractice.interviewerFollowUp')}:</strong> {followUp}
                </div>
              )}

              {/* Question card */}
              <div className="ip-question-card">
                <span className={`ip-category-badge ip-cat-${currentQuestion.category}`}>
                  {currentQuestion.category}
                </span>
                <span className={`ip-difficulty-badge ip-diff-${currentQuestion.difficulty}`}>
                  {currentQuestion.difficulty}
                </span>
                <h2 className="ip-question-text">{currentQuestion.text}</h2>
              </div>

              {/* Answer area */}
              <textarea
                className="ip-answer-area"
                rows={6}
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                placeholder={t('interviewPractice.answerPlaceholder')}
                disabled={submitting}
              />

              <div className="ip-interview-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleSubmitAnswer}
                  disabled={submitting || !answerText.trim()}
                >
                  {submitting
                    ? t('interviewPractice.submitting')
                    : currentQuestionIndex < session.questions.length - 1
                      ? t('interviewPractice.nextQuestion')
                      : t('interviewPractice.finishInterview')}
                </button>
                <button className="btn btn-secondary" onClick={handleEndEarly} disabled={loading}>
                  {t('interviewPractice.endEarly')}
                </button>
              </div>
            </>
          )}

          {/* ---- VOICE MODE ---- */}
          {inputMode === 'voice' && (
            <div className="ip-voice-interview">
              {/* Pre-connect: device selection */}
              {!voiceSessionStarted && (
                <div className="ip-voice-setup">
                  <div className="ip-voice-icon-large">&#127908;</div>
                  <h2>{t('interviewPractice.voiceReady')}</h2>
                  <p className="ip-voice-subtitle">{t('interviewPractice.voiceReadyDesc')}</p>

                  {availableDevices.length > 0 && (
                    <div className="ip-voice-devices">
                      {availableDevices.map((device) => (
                        <label key={device.deviceId} className="ip-voice-device">
                          <input
                            type="radio"
                            name="voice-device"
                            value={device.deviceId}
                            defaultChecked={false}
                          />
                          <span>{device.label}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  <button
                    className="btn btn-primary ip-voice-start-btn"
                    onClick={() => {
                      const selected = document.querySelector<HTMLInputElement>('input[name="voice-device"]:checked');
                      handleStartVoice(selected?.value || undefined);
                    }}
                  >
                    {t('interviewPractice.startVoiceInterview')}
                  </button>
                </div>
              )}

              {/* Connecting */}
              {voiceSessionStarted && voiceState === 'connecting' && (
                <div className="ip-voice-connecting">
                  <div className="spinner" />
                  <p>{t('interviewPractice.voiceConnecting')}</p>
                </div>
              )}

              {/* Active voice session */}
              {voiceSessionStarted && voiceState === 'active' && (
                <div className="ip-voice-active">
                  {/* Voice orb with mic level */}
                  <div className="ip-voice-orb-container">
                    <div
                      className="ip-voice-orb"
                      style={{ transform: `scale(${1 + micLevel / 150})` }}
                    />
                    <div className="ip-voice-orb-label">
                      {t('interviewPractice.voiceListening')}
                    </div>
                  </div>

                  {/* Live transcript */}
                  <div className="ip-voice-transcript">
                    {voiceTranscript.length === 0 && (
                      <p className="ip-voice-transcript-empty">
                        {t('interviewPractice.voiceTranscriptHint')}
                      </p>
                    )}
                    {voiceTranscript.map((entry, i) => (
                      <div
                        key={i}
                        className={`ip-voice-entry ip-voice-entry-${entry.role}`}
                      >
                        <span className="ip-voice-entry-role">
                          {entry.role === 'user'
                            ? t('interviewPractice.voiceYou')
                            : t('interviewPractice.voiceInterviewer')}
                        </span>
                        <span className="ip-voice-entry-text">{entry.text}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    className="btn btn-secondary ip-voice-end-btn"
                    onClick={handleEndVoiceInterview}
                    disabled={loading}
                  >
                    {t('interviewPractice.endVoiceInterview')}
                  </button>
                </div>
              )}

              {/* Error state */}
              {voiceSessionStarted && voiceState === 'error' && (
                <div className="ip-voice-error-state">
                  <p>{voiceError || t('interviewPractice.voiceError')}</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setVoiceSessionStarted(false);
                      setError('');
                    }}
                  >
                    {t('interviewPractice.voiceRetry')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* -------- DEBRIEF PHASE -------- */}
      {phase === 'debrief' && (
        <div className="ip-debrief-phase">
          {loading && (
            <div className="ip-loading">
              <div className="spinner" />
              <p>{t('interviewPractice.generatingDebrief')}</p>
            </div>
          )}
          {error && !debrief && <div className="ip-error">{error}</div>}
          {debrief && <InterviewDebriefView debrief={debrief} session={session} />}
          <div className="ip-debrief-actions">
            <button className="btn btn-primary" onClick={() => { setPhase('setup'); setSession(null); setDebrief(null); setError(''); setVoiceSessionStarted(false); }}>
              {t('interviewPractice.practiceAgain')}
            </button>
            <button className="btn btn-secondary" onClick={() => navigate('/resume-tuner')}>
              {t('interviewPractice.backToTuner')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
